import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/_authed/admin')({
  component: AdminDashboard,
});

interface Metrics {
  dau: number;
  wau: number;
  mau: number;
  interventions: { offered: number; completed: number; acceptanceRate: number };
  safetyFires14d: Array<{ day: number; count: number }>;
  latencies: Array<{ path: string; p95: number; count: number }>;
  federationEnabled: boolean;
}

function AdminDashboard() {
  const { data, error } = useQuery<Metrics>({
    queryKey: ['admin-metrics'],
    queryFn: async () => {
      const r = await fetch('/api/admin/metrics', { credentials: 'include' });
      if (r.status === 403) throw new Error('admin only');
      if (!r.ok) throw new Error(`metrics ${r.status}`);
      return r.json();
    },
  });

  if (error) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12">
        <h1 className="text-2xl font-semibold tracking-tight">admin</h1>
        <p className="mt-4 text-sm text-destructive">{(error as Error).message}</p>
      </section>
    );
  }
  if (!data) {
    return <section className="mx-auto max-w-3xl px-4 py-12">loading…</section>;
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">admin</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        operational metrics. nothing here identifies a user.
      </p>

      <section className="mt-8 grid gap-3 sm:grid-cols-3">
        <Stat label="DAU" value={data.dau} />
        <Stat label="WAU (7d)" value={data.wau} />
        <Stat label="MAU (30d)" value={data.mau} />
      </section>

      <section className="mt-8 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground">interventions (30d)</h2>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <Stat label="offered" value={data.interventions.offered} small />
          <Stat label="completed" value={data.interventions.completed} small />
          <Stat
            label="acceptance"
            value={`${(data.interventions.acceptanceRate * 100).toFixed(1)}%`}
            small
          />
        </div>
      </section>

      <section className="mt-6 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground">safety routing fires (14d)</h2>
        <ul className="mt-3 space-y-1 text-sm">
          {data.safetyFires14d.length === 0 ? (
            <li className="text-muted-foreground">none.</li>
          ) : (
            data.safetyFires14d.map((d) => (
              <li key={d.day} className="flex justify-between">
                <span className="font-mono">{String(d.day)}</span>
                <span>{d.count}</span>
              </li>
            ))
          )}
        </ul>
      </section>

      <section className="mt-6 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground">p95 latency (24h)</h2>
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th className="text-left font-normal">path</th>
              <th className="text-right font-normal">p95</th>
              <th className="text-right font-normal">requests</th>
            </tr>
          </thead>
          <tbody>
            {data.latencies.map((l) => (
              <tr key={l.path}>
                <td className="font-mono py-1">{l.path}</td>
                <td className="text-right tabular-nums">{l.p95} ms</td>
                <td className="text-right tabular-nums text-muted-foreground">{l.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-6 text-xs text-muted-foreground">
        federation: {data.federationEnabled ? 'enabled' : 'off (default)'}
      </section>
    </section>
  );
}

function Stat({ label, value, small }: { label: string; value: number | string; small?: boolean }) {
  return (
    <div className={`rounded-[var(--radius)] border border-border p-4`}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 ${small ? 'text-xl' : 'text-3xl'} font-semibold tabular-nums`}>{value}</div>
    </div>
  );
}
